import * as React from 'react';
import {useSelector} from 'react-redux';

import {selectAgencyData} from 'src/selectors/agency';
import {captureSentryMessage} from 'src/utils/sentry';
import {
  buildByodGlobalPayload,
  hasBoundedDateWindow,
} from 'src/components/analytics-genesis/byod/utils/build-byod-global-payload';
import type {ByodFilterMap} from 'src/types/byod';

import type {ByodDashboardViewDateRange} from './byod-dashboard-view.types';


export type UseByodEmbedPayloadArgs = {
  dashboardId: string;
  dateRange: ByodDashboardViewDateRange;
  globalPayload: ByodFilterMap;
};

export type UseByodEmbedPayloadReturn = {
  /**
   * False when a required date bound is missing — a host integration bug. The
   * view renders a failure state instead of issuing an unbounded `/query`.
   */
  isDateWindowBounded: boolean;
  /**
   * Payload injected into every tile's `/query`: the host's timezone-stamped
   * date window plus opaque host filters. The bounds override any date keys in
   * `globalPayload`.
   */
  effectivePayload: ByodFilterMap;
};

/**
 * Owns the embed's per-instance query inputs so the view stays presentational:
 * validate the host's date window (reporting a missing bound to Sentry), and
 * build the effective payload injected into every tile. Keeps the date/filter
 * transform and its telemetry out of the rendering component.
 */
export function useByodEmbedPayload({
  dashboardId,
  dateRange,
  globalPayload,
}: UseByodEmbedPayloadArgs): UseByodEmbedPayloadReturn {
  const {timezone: agencyTimezone} = useSelector(selectAgencyData);

  // No fabricated default window: the date bounds are required by the prop type,
  // so a missing/empty bound here means the type contract was bypassed upstream
  // (e.g. URL/redux state typed `string` but actually undefined at runtime).
  // Alert loudly via Sentry rather than silently masking it with a default —
  // an unbounded /query is a host bug we want to see, not paper over. In an
  // effect because Sentry is a side effect that must not run during render.
  const isDateWindowBounded = hasBoundedDateWindow(dateRange);
  const reportedUnboundedForRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isDateWindowBounded) {
      if (reportedUnboundedForRef.current !== dashboardId) {
        captureSentryMessage(
          'BYOD embed received a date window missing a start/end bound',
          {
            level: 'error',
            tags: {feature: 'byod', context: 'embed-date-window'},
            extra: {
              dashboardId,
              startDate: dateRange.startDate ?? null,
              endDate: dateRange.endDate ?? null,
            },
          },
        );
        reportedUnboundedForRef.current = dashboardId;
      }
    } else {
      reportedUnboundedForRef.current = null;
    }
  }, [
    isDateWindowBounded,
    dashboardId,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  // Apply the host's date window (timezone-stamped) + opaque filters into the
  // payload injected into every tile. The embed owns the transform so hosts just
  // pass plain calendar dates; the bounds override any date keys in globalPayload.
  const effectivePayload = React.useMemo<ByodFilterMap>(
    () =>
      buildByodGlobalPayload({
        dateRange: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          timezone: dateRange.timezone || agencyTimezone,
        },
        filters: globalPayload,
      }),
    [
      dateRange.startDate,
      dateRange.endDate,
      dateRange.timezone,
      agencyTimezone,
      globalPayload,
    ],
  );

  return {isDateWindowBounded, effectivePayload};
}
